import { Controller } from "@hotwired/stimulus"
import ComposerFilePicker from "../utils/composer_file_picker"

// Composer attachments for shared/_message_composer.html.slim (Channels,
// Connections, Dating, dinner party, language buddy, resume coach, the
// merged per-person feed — every human-to-human/bot thread that renders the
// shared composer, per FILES_FEATURE_SPEC.md). Unlike assistant_compose's
// version of this same idea, THIS composer is a plain native form submit
// (form_with local: true, no fetch/JSON) — so staged files must actually
// ride inside the real <input type=file> for a normal submit (or the
// Messages drawer's `new FormData(form)` intercept) to send them, rather
// than being reconstructed at submit time.
//
// A native file input's FileList is replaced wholesale by every OS picker
// interaction, so each `change` immediately gets merged into the running
// `_attachments` list via a fresh DataTransfer — see _syncFileInput. Library
// picks carry no bytes at all: each becomes its own hidden
// `user_file_ids[]` input appended straight onto the form.
export default class extends Controller {
    static targets = ["fileInput", "attachPanel", "attachSearchInput", "attachResults", "attachmentsRow"]
    static values = { filesIndexUrl: String, filesSearchUrl: String }

    connect() {
        this._attachments = []
        this._attachSeq = 0
        this._filePicker = new ComposerFilePicker(this)
    }

    disconnect() {
        this._filePicker.disconnect()
    }

    pickFile() {
        if (this.hasFileInputTarget) this.fileInputTarget.click()
    }

    filesChosen(e) {
        Array.from(e.target.files || []).forEach((file) => {
            // An image gets a real thumbnail in the staged row rather than a
            // paperclip and a filename — you should be able to see WHICH photo
            // you attached before you post it. The object URL is revoked in
            // removeAttachment/_renderAttachments' teardown; leaving them
            // alive would pin every previewed file in memory for the life of
            // the page.
            const previewUrl = file.type?.startsWith("image/") ? URL.createObjectURL(file) : null
            this._attachments.push({ localId: `up-${++this._attachSeq}`, kind: "upload", file, name: file.name, previewUrl, ownsPreview: Boolean(previewUrl) })
        })
        this._syncFileInput()
        this._renderAttachments()
    }

    openFilePicker() {
        this._filePicker.openFilePicker()
    }

    closeAttachPanel() {
        this._filePicker.closeAttachPanel()
    }

    searchFiles() {
        this._filePicker.searchFiles()
    }

    attachSearchKeydown(event) {
        this._filePicker.attachSearchKeydown(event)
    }

    // A library pick carries only an id — the server re-resolves it against
    // current_user.user_files (never a bare id lookup), so a stale or
    // someone-else's id just silently attaches nothing. The id rides to the
    // server as a plain hidden field appended straight onto the form, since
    // this composer is a native submit rather than a hand-built FormData.
    pickLibraryFile(e) {
        const id = e.currentTarget.dataset.id
        const name = e.currentTarget.dataset.name
        // Present only for image files (see UserFilesController's row payload).
        const previewUrl = e.currentTarget.dataset.thumbUrl || null
        if (!id) return
        if (this._attachments.some((a) => a.kind === "library" && a.id === id)) {
            this.closeAttachPanel()
            return
        }

        const input = document.createElement("input")
        input.type = "hidden"
        input.name = "user_file_ids[]"
        input.value = id
        this.element.appendChild(input)

        this._attachments.push({ localId: `lib-${id}`, kind: "library", id, name, inputEl: input, previewUrl, ownsPreview: false })
        this.closeAttachPanel()
        this._renderAttachments()
    }

    removeAttachment(e) {
        const localId = e.currentTarget.dataset.localId
        const removed = this._attachments.find((a) => a.localId === localId)
        if (removed?.inputEl) removed.inputEl.remove()
        if (removed?.ownsPreview && removed.previewUrl) URL.revokeObjectURL(removed.previewUrl)
        this._attachments = this._attachments.filter((a) => a.localId !== localId)
        this._syncFileInput()
        this._renderAttachments()
    }

    // Rebuilds the real file input's FileList from every staged "upload" chip.
    // Needed because a native input's FileList is replaced wholesale by each
    // OS picker interaction (and by removing one chip) — DataTransfer is the
    // only way to hand a browser a FileList assembled from JS-held Files.
    _syncFileInput() {
        if (!this.hasFileInputTarget) return
        const transfer = new DataTransfer()
        this._attachments.filter((a) => a.kind === "upload").forEach((a) => transfer.items.add(a.file))
        this.fileInputTarget.files = transfer.files
    }

    _renderAttachments() {
        if (!this.hasAttachmentsRowTarget) return
        const row = this.attachmentsRowTarget
        row.innerHTML = ""
        row.hidden = this._attachments.length === 0
        this._attachments.forEach((a) => {
            const chip = document.createElement("span")
            // An image stages as a thumbnail tile with the remove control
            // floating on it; everything else keeps the icon-and-filename pill,
            // which is the right shape for a PDF and the wrong one for a photo.
            chip.className = a.previewUrl ? "composer-attachment-chip composer-attachment-chip--image" : "composer-attachment-chip"
            if (a.previewUrl) {
                const thumb = document.createElement("img")
                thumb.className = "composer-attachment-thumb"
                thumb.src = a.previewUrl
                thumb.alt = a.name
                chip.appendChild(thumb)
            } else {
                const icon = document.createElement("i")
                icon.className = a.kind === "upload" ? "bi bi-paperclip" : "bi bi-folder2"
                icon.setAttribute("aria-hidden", "true")
                const label = document.createElement("span")
                label.className = "composer-attachment-chip-name"
                label.textContent = a.name
                chip.appendChild(icon)
                chip.appendChild(label)
            }
            const remove = document.createElement("button")
            remove.type = "button"
            remove.className = "composer-attachment-chip-remove"
            remove.setAttribute("aria-label", `Remove ${a.name}`)
            remove.dataset.localId = a.localId
            remove.dataset.action = "click->message-attach#removeAttachment"
            remove.innerHTML = '<i class="bi bi-x" aria-hidden="true"></i>'
            chip.appendChild(remove)
            row.appendChild(chip)
        })
    }
}
